import { toast } from '@/components/toast';
import { didApiUrl, mixpanelKey, webSocketUrl } from '@/environment';
import { useUpdatePresenterImg } from '@/pages/agents/hooks';
import { loadAgentsClientScript } from '@/pages/agents/loadAgentsClientScript';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const maxWidth = '768px';

interface AgentContainerProps {
    isOwner: boolean;
    isMobile?: boolean;
    isChatOpen?: boolean;
    isV2?: boolean;
}

const AgentContainer = styled.div<AgentContainerProps>`
    position: relative;
    overflow: hidden;
    user-select: text;
    outline: none;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 16px;
    isolation: isolate;

    ${({ isV2, isMobile, isChatOpen }) =>
        isV2
            ? `
        aspect-ratio: ${isMobile ? '9 / 16' : isChatOpen ? '9 / 10' : '16 / 9'};
        ${isMobile ? 'height' : 'width'}: 100%;
        ${!isMobile ? 'height' : 'width'}: auto;
        margin-${isMobile ? 'left' : 'top'}: ${isMobile ? 'auto' : '0'};
        margin-${isMobile ? 'right' : 'bottom'}: auto;
        transition: ${isMobile ? 'aspect-ratio 0.3s' : 'none'};
    `
            : `
        min-width: 400px;
        max-width: 400px;
        max-height: 700px;
        margin-bottom: 20px;
        top: 50%;
        transform: translateY(-50%);
        flex: 1;
        height: 100%;
    `}

    ${({ isOwner }) =>
        !isOwner &&
        `@media (max-width: ${maxWidth}) {
            min-width: 350px;
            width: 100%;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            border-radius: 0;
            border: 0;
            margin: 0;
    }`}
`;
interface PlaygroundProps {
    agentId: string;
    clientKey?: string;
    isMobile?: boolean;
}

export const Playground = observer(({ agentId, clientKey, isMobile }: PlaygroundProps) => {
    const [isChatOpen, setIsChatOpen] = useState(false);

    const targetRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { loginWithRedirect, isAnonymous, user } = useAuth();
    useUpdatePresenterImg();

    const isV2 = agentId.startsWith('v2_');

    useEffect(() => {
        const handleChatToggle = (event: Event) => {
            // Type guard to ensure it's a CustomEvent with a boolean detail
            if (event instanceof CustomEvent && typeof event.detail === 'boolean') {
                setIsChatOpen(event.detail);
            }
        };

        window.addEventListener('didagent_chat_toggled', handleChatToggle as EventListener);

        return () => {
            window.removeEventListener('didagent_chat_toggled', handleChatToggle as EventListener);
        };
    }, []);

    useEffect(() => {
        if (!targetRef || !targetRef?.current) {
            console.warn('No target ref for playground');
            return;
        }

        if (!agentId) {
            console.warn('No agent id for playground');
            navigate(AppRoutes.Agent);
        } else {
            if (clientKey) {
                loadAgentsClientScript({ isV2 }).then(({ init }) => {
                    init({
                        agentId,
                        clientKey,
                        mode: 'full',
                        chatMode: 'Playground' as any,
                        targetElement: targetRef.current!,
                        onError(err: Error) {
                            console.log(err);

                            try {
                                const jsonError = JSON.parse(err.message);
                                if (jsonError.kind === 'RequestTimeoutError') {
                                    toast.error({
                                        toastId: 'timeout',
                                        title: 'Request timeout, please try again later.',
                                    });
                                } else if (jsonError.description === 'Agent not found') {
                                    toast.error({
                                        toastId: 'agent-not-found',
                                        title: 'The agent you are trying to access does not exist.',
                                    });

                                    navigate(AppRoutes.Agent);
                                }
                            } catch {}

                            if (err.message === 'Playground chat limit exceeded') {
                                toast.error({
                                    toastId: 'chat-limit-exceeded',
                                    title: 'Chat limit reached. Please continue on the chat page.',
                                });
                            }
                        },
                        offline: {
                            onSend: () => {
                                mixpanel?.track('go-to-signup', { event: 'click', from: 'agent-playground', isAnonymous });
                                loginWithRedirect({ redirectUri: window.location.href });
                            },
                        },
                        didSocketApiUrl: webSocketUrl,
                        didApiUrl,
                        track: false,
                        monitor: false,
                        customMixpanelKey: mixpanelKey,
                        externalId: user?.sub ?? '',
                        mixpanelAdditionalProperties: {
                            plan: user?.planGroup,
                        },
                    });
                });
            } else {
                toast.error({
                    toastId: 'invalid key',
                    title: 'Missing agent key',
                });
            }
        }
    }, [agentId, clientKey]);

    return <AgentContainer ref={targetRef} isOwner={false} isMobile={isMobile} isChatOpen={isChatOpen} isV2={isV2} />;
});
